import { gql } from "@apollo/client";
import { useQuery, useMutation } from "@apollo/client/react";
import {
  REMOVE_ORGANIZATION_DEFAULT_TEMPLATE,
  REMOVE_TEAM_DEFAULT_TEMPLATE,
} from "@graphql/mutations/AutoTemplates";
import { GET_AUTO_TEMPLATES } from "@graphql/queries/AutoTemplates";
import { useWatchUserWsEvent } from "@hooks";
import {
  createContext,
  useContext,
  useEffect,
  useEffectEvent,
} from "react";

import { useAuth } from "./use-auth";
import { useOrganization } from "./use-organization";
import { useTeam } from "./use-team";

const GET_TEAM_DEFAULT_TEMPLATES = gql`
  query GetTeamDefaultTemplates($teamUuid: String!) {
    team(uuid: $teamUuid) {
      uuid
      defaultTemplates {
        uuid
        name
        templateType
        description
        isFavourite
        sharingType
        teamDefaults
        createdAt
        updatedAt
        user {
          uuid
          firstName
          lastName
          teams {
            organization {
              uuid
            }
            uuid
            name
          }
        }
      }
    }
  }
`;

const GET_ORGANIZATION_DEFAULT_TEMPLATES = gql`
  query GetOrganizationDefaultTemplates($orgUuid: String!) {
    organization(uuid: $orgUuid) {
      uuid
      defaultTemplates {
        uuid
        name
        templateType
        description
        isFavourite
        sharingType
        createdAt
        updatedAt
        user {
          uuid
          firstName
          lastName
          teams {
            uuid
            name
            organization {
              uuid
            }
          }
        }
      }
    }
  }
`;

const EMPTY_TEMPLATES = [];
const TemplatesContext = createContext({
  defaultTemplatesForSelectedTeam: [],
  loadingTeamDefaults: false,
  teamDefaultsError: null,
  refetchTeamDefaults: () => {},
  defaultTemplatesForSelectedOrg: [],
  loadingOrgDefaults: false,
  orgDefaultsError: null,
  refetchOrgDefaults: () => {},
  teamAndOrgDefaultTemplates: [],
  refetchDefaultTemplates: () => {},
  removeTeamDefault: async () => {},
  removeOrgDefault: async () => {},
  sharedTemplates: [],
  ownedTemplates: [],
  allTemplates: [],
  loadingTemplates: false,
  templatesError: null,
  refetchTemplates: async () => {},
});

export const TemplatesProvider = ({ children }) => {
  const { currentTeam } = useTeam();
  const { orgUuid } = useOrganization();
  const { userUuid } = useAuth();

  const {
    data: templatesData,
    loading: loadingTemplates,
    error: templatesError,
    refetch: refetchTemplates,
  } = useQuery(GET_AUTO_TEMPLATES, {
    variables: { teamUuid: currentTeam?.uuid || null },
    skip: !userUuid,
    fetchPolicy: "cache-and-network",
  });

  const {
    data: teamDefaultsData,
    loading: loadingTeamDefaults,
    error: teamDefaultsError,
    refetch: refetchTeamDefaultsQuery,
  } = useQuery(GET_TEAM_DEFAULT_TEMPLATES, {
    variables: { teamUuid: currentTeam?.uuid },
    skip: !currentTeam?.uuid,
  });

  const {
    data: orgDefaultsData,
    loading: loadingOrgDefaults,
    error: orgDefaultsError,
    refetch: refetchOrgDefaultsQuery,
  } = useQuery(GET_ORGANIZATION_DEFAULT_TEMPLATES, {
    variables: { orgUuid: currentTeam?.orgUuid },
    skip: !currentTeam?.orgUuid,
  });

  useWatchUserWsEvent({
    onMessage: refetchTemplates,
    eventName: "companion_llm_template_creation",
  });

  const refetchTeamDefaults = () => {
    if (currentTeam?.uuid) {
      return refetchTeamDefaultsQuery();
    }
  };

  const refetchOrgDefaults = () => {
    if (currentTeam?.orgUuid) {
      return refetchOrgDefaultsQuery();
    }
  };

  const allTemplates =
    templatesData?.autoTemplates || EMPTY_TEMPLATES;

  const ownedTemplates = allTemplates.filter(
    (template) => template.user?.uuid === userUuid,
  );

  const sharedTemplates = allTemplates.filter(
    (template) => template.user?.uuid !== userUuid,
  );

  const defaultTemplatesForSelectedTeam =
    teamDefaultsData?.team?.defaultTemplates || EMPTY_TEMPLATES;

  const defaultTemplatesForSelectedOrg =
    orgDefaultsData?.organization?.defaultTemplates ||
    EMPTY_TEMPLATES;

  // Consolidate team and org defaults and remove duplicates
  const teamAndOrgDefaultTemplates = (() => {
    const templateMap = new Map();

    defaultTemplatesForSelectedOrg.forEach((template) => {
      templateMap.set(template.uuid, template);
    });

    defaultTemplatesForSelectedTeam.forEach((template) => {
      if (!templateMap.has(template.uuid)) {
        templateMap.set(template.uuid, template);
      }
    });

    return Array.from(templateMap.values());
  })();

  const [removeTeamDefaultMutation] = useMutation(
    REMOVE_TEAM_DEFAULT_TEMPLATE,
  );
  const [removeOrgDefaultMutation] = useMutation(
    REMOVE_ORGANIZATION_DEFAULT_TEMPLATE,
  );

  const refetchDefaultTemplates = () => {
    refetchTeamDefaults();
    refetchOrgDefaults();
  };

  const onTeamOrOrgChange = useEffectEvent(() => {
    refetchDefaultTemplates();
  });

  const removeTeamDefault = async (templateUuid) => {
    if (!currentTeam?.uuid) {
      throw new Error("No team selected");
    }
    await removeTeamDefaultMutation({
      variables: {
        templateUuid,
        teamUuids: [currentTeam.uuid],
      },
    });
    refetchDefaultTemplates();
  };

  const removeOrgDefault = async (templateUuid) => {
    if (!orgUuid) {
      throw new Error("No organization found");
    }
    await removeOrgDefaultMutation({
      variables: {
        templateUuid,
        orgUuid,
      },
    });
    refetchDefaultTemplates();
  };

  useEffect(() => {
    onTeamOrOrgChange();
  }, [currentTeam?.uuid, currentTeam?.orgUuid]);

  const findTemplateByUuid = (templateUuid) => {
    if (!templateUuid || !allTemplates?.length) return null;
    return allTemplates.find((t) => t.uuid === templateUuid);
  };

  const value = {
    defaultTemplatesForSelectedTeam,
    loadingTeamDefaults,
    teamDefaultsError,
    refetchTeamDefaults,
    defaultTemplatesForSelectedOrg,
    loadingOrgDefaults,
    orgDefaultsError,
    refetchOrgDefaults,
    teamAndOrgDefaultTemplates,
    refetchDefaultTemplates,
    removeTeamDefault,
    removeOrgDefault,
    sharedTemplates,
    ownedTemplates,
    allTemplates,
    loadingTemplates,
    templatesError,
    refetchTemplates,
    findTemplateByUuid,
  };

  return (
    <TemplatesContext.Provider value={value}>
      {children}
    </TemplatesContext.Provider>
  );
};

export const useTemplates = () => {
  return useContext(TemplatesContext);
};
